// TicketNotes.js
import React, { useState, useEffect, useRef } from "react";
import { useHttpClient } from "../../shared/hooks/http-hook";
import dateFormat, { masks } from "dateformat";

export default function TicketNotes({ ticketId, authContext, notes = [] }) {
	const { sendRequest, isLoading } = useHttpClient();
	const [allNotes, setAllNotes] = useState(notes);
	const [newMessage, setNewMessage] = useState("");
  const messagesContainerRef = useRef(null);

	masks.displayDate = "mmm d, h:mm tt";

	// Keep local notes in sync if parent passes in updated notes
	useEffect(() => {
		setAllNotes(notes);
	}, [notes]);

  useEffect(() => {
    scrollToBottom();
  }, [allNotes]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

	const handleSend = async () => {
		if (!newMessage.trim()) return;

		try {
			// 1) Send POST request to your server to add a new note
			// Adjust endpoint/headers as needed
			const response = await sendRequest(
				`/api/tickets/${ticketId}/notes`,
				"POST",
				JSON.stringify({
					note: newMessage,
				}),
				{
					"Content-Type": "application/json",
					Authorization: "Bearer " + authContext.token,
				}
			);

			setAllNotes(response.data || []);

			setNewMessage("");
		} catch (err) {
			console.error("Error adding note:", err);
			// Handle error UI, e.g. toast or setErrorMsg
		}
	};

	// Render each note as a bubble in a scrollable container
	return (
		<div className="flex flex-col border rounded h-[400px] w-full">
			{/* Scrollable messages container */}
			<div
        className="flex-grow overflow-y-auto p-3 space-y-2"
        ref={messagesContainerRef}
        >
				{allNotes.map((note, index) => {
					const isMyNote = note.userId === authContext.userId;
					return (
						<div
							key={index}
							className={`flex ${isMyNote ? "justify-end" : "justify-start"}`}
						>
							<div
								className={`rounded px-3 py-2 max-w-xs break-words ${
									isMyNote ? "bg-blue-100" : "bg-gray-100"
								}`}
							>
								{/* The message text */}
								<div className="text-sm">{note.message}</div>
								{/* (Optional) The date/time in smaller font */}
								<div className="text-xs text-gray-500 mt-1">
									{note.createdAt
										? dateFormat(
												new Date(note.createdAt._seconds * 1000),
												"displayDate"
										  )
										: ""}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Input + Send button row */}
			<div className="border-t p-3 flex items-center space-x-2">
				<input
					type="text"
					className="flex-grow border rounded px-3 py-2"
					placeholder="Type a note..."
					value={newMessage}
					onChange={(e) => setNewMessage(e.target.value)}
				/>
				<button
					className="bg-blue-600 text-white px-3 py-2 rounded disabled:opacity-50"
					onClick={handleSend}
					disabled={!newMessage.trim() || isLoading}
				>
					{isLoading ? "Sending..." : "Send"}
				</button>
			</div>
		</div>
	);
}
